/**
 * @voltxampere/presets — Voltage doubler (Sprint 50-3)
 *
 * Greinacher voltage doubler — two diodes + two capacitors clamp
 * the AC peak to negative ground on one half-cycle, then sum
 * V_peak with the DC charge to produce ~2 × V_peak across the
 * load. With V_AC peak 10V, ideal V_out ≈ 20V (minus 2 × diode
 * drop ≈ 18.6V at steady state).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const voltageDoubler = {
    id: "voltage-doubler",
    name: { tr: "Gerilim İkileyici", en: "Voltage Doubler" },
    category: "power",
    difficulty: 3,
    description: {
        tr: "İki diyot + iki kondansatör ile AC tepe gerilimini ikiye katlama (Greinacher).",
        en: "Two diodes + two caps double the AC peak voltage (Greinacher topology).",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "Voltage Doubler",
        description: "Greinacher AC peak doubler",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "ac_in" }),
            makeNet({ name: "mid" }),
            makeNet({ name: "v_out" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 10 },
                terminals: { pos: "net_ac_in", neg: "net_gnd" },
                label: "V1",
                position: { x: -160, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "C1",
                type: "capacitor",
                params: { capacitance: 1e-6 },
                terminals: { p1: "net_ac_in", p2: "net_mid" },
                label: "C1",
                position: { x: -60, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "D1",
                type: "diode",
                params: { is: 1e-12, n: 1.0 },
                terminals: { anode: "net_gnd", cathode: "net_mid" },
                label: "D1",
                position: { x: -60, y: 60 },
                rotation: 90,
            }),
            makeComponent({
                id: "D2",
                type: "diode",
                params: { is: 1e-12, n: 1.0 },
                terminals: { anode: "net_mid", cathode: "net_v_out" },
                label: "D2",
                position: { x: 40, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "C2",
                type: "capacitor",
                params: { capacitance: 1e-6 },
                terminals: { p1: "net_v_out", p2: "net_gnd" },
                label: "C2",
                position: { x: 120, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -160, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            {
                id: "w_ac_in",
                netId: "net_ac_in",
                path: [
                    { x: -160, y: -10 },
                    { x: -160, y: -40 },
                    { x: -80, y: -40 },
                    { x: -80, y: 0 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_mid",
                netId: "net_mid",
                path: [
                    { x: -40, y: 0 },
                    { x: 20, y: 0 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_v_out",
                netId: "net_v_out",
                path: [
                    { x: 60, y: 0 },
                    { x: 120, y: 0 },
                    { x: 120, y: -20 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_gnd",
                netId: "net_gnd",
                path: [
                    { x: -160, y: 10 },
                    { x: -160, y: 71 },
                    { x: 120, y: 71 },
                    { x: 120, y: 20 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "v_out",
            analysis: "dc",
            value: 0,
            tolerance: 1.0,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "AC gerilimini DC seviyesine kaydırma ve toplama.",
            en: "Shift AC voltage to a DC level and sum amplitudes.",
        },
        {
            tr: "Kondansatör şarj/deşarj davranışıyla DC üretimi.",
            en: "DC generation via capacitor charge/discharge cycles.",
        },
    ],
    references: ["Horowitz/Hill, The Art of Electronics, §1.8"],
};
//# sourceMappingURL=voltage-doubler.js.map